import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { DirectoryTree } from './DirectoryTree';
import type { FolderTreeResponse, MoveDestinationDialogProps } from '../AssetExplorer.types';

export const MoveDestinationDialog: React.FC<MoveDestinationDialogProps> = ({
    open,
    onClose,
    folders,
    folderTreeLoading,
    invalidDestinationIds,
    onConfirm,
    confirmLoading,
}) => {
    const theme = useTheme();
    const [destination, setDestination] = useState<FolderTreeResponse | null>(null);

    useEffect(() => {
        if (!open) {
            setDestination(null);
        }
    }, [open]);

    const handleSelect = (folder: FolderTreeResponse) => {
        if (invalidDestinationIds.has(folder._id)) {
            return;
        }
        setDestination(folder);
    };

    const canConfirm = Boolean(destination?._id) && !confirmLoading;

    return (
        <Dialog
            open={open}
            onClose={confirmLoading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, maxHeight: '90vh' },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DriveFileMoveIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Move to folder
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, minHeight: 280 }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.06) }}>
                    <Typography variant="body2" color="text.secondary">
                        Select the destination folder. The tree matches your Asset Explorer sidebar. Folders you are moving
                        cannot be chosen as the destination.
                    </Typography>
                </Box>
                <Box
                    sx={{
                        maxHeight: 360,
                        overflow: 'auto',
                        px: 1,
                        py: 1,
                    }}
                >
                    <DirectoryTree
                        pickerMode
                        disabledFolderIds={invalidDestinationIds}
                        folders={folders}
                        selectedFolderId={destination?._id ?? null}
                        onFolderSelect={handleSelect}
                        onFolderCreate={() => undefined}
                        onFolderDelete={() => undefined}
                        loading={folderTreeLoading}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={confirmLoading}
                    variant="outlined"
                    color="secondary"
                    sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    disabled={!canConfirm}
                    onClick={() => destination && onConfirm(destination._id)}
                    startIcon={
                        confirmLoading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <DriveFileMoveIcon />
                        )
                    }
                >
                    {confirmLoading ? 'Moving…' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MoveDestinationDialog;
